import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Copy,
  ExternalLink,
  Info,
  LogOut,
  MoreHorizontal,
  Smartphone,
  Sparkles,
  Users,
} from "lucide-react";
import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from "react";
import { toast } from "sonner";
import { DeviceConsole } from "@/components/device-console";
import { PageHeader } from "@/components/page-header";
import {
  Countdown,
  ReservationKeeper,
  useReservationKeeper,
} from "@/components/reservation-keeper";
import { SessionEndedDialog } from "@/components/session-ended-dialog";
import { SidePanel } from "@/components/side-panel";
import { DeviceStatusLabel, StatusDot } from "@/components/status-dot";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogInset,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useDeviceStream } from "@/hooks/use-device-stream";
import { usePopoutPresence } from "@/hooks/use-popout-presence";
import type { ReservationRenewal } from "@/hooks/use-reservation-renewal";
import { useSessionEnded } from "@/hooks/use-session-ended";
import { openPopout } from "@/lib/popout";
import { loadPanelOpen, savePanelOpen } from "@/lib/side-panels";
import { trpc } from "@/lib/trpc";
import type { RouterOutputs } from "@/lib/types";
import { cn, platformLabel, relativeTime } from "@/lib/utils";

export const Route = createFileRoute("/_app/devices/$deviceId")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(
      context.trpc.device.get.queryOptions({ id: params.deviceId }),
    ),
  component: DevicePage,
});

function DevicePage() {
  const { deviceId } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  // Without this the detail page got no live updates at all: a device released
  // from elsewhere, or taken back, left this page showing a stale header.
  const { pollInterval } = useDeviceStream();
  const { data: device } = useQuery({
    ...trpc.device.get.queryOptions({ id: deviceId }),
    refetchInterval: pollInterval,
  });
  const { data: me } = useQuery(trpc.user.me.queryOptions());
  const { data: policy } = useQuery(trpc.settings.public.queryOptions());
  // Both sides have to exist. Compared bare, a free device and a `me` still
  // loading are `undefined === undefined`: the page briefly believed it held
  // the device, and a hard reload of a free one opened "Session ended".
  const mine = Boolean(me && device?.reservation && device.reservation.userId === me.id);
  /** Openly joined this session: full control, no reservation. */
  const observing = Boolean(
    me && device?.reservation?.observers.some((o) => o.userId === me.id) && !mine,
  );
  /** Whether there is a live session here — what the rail and screen need. */
  const inSession = mine || observing;

  // Only worth asking about while somebody else has it and we are not already
  // in. The device stream invalidates this along with everything else.
  const canAsk = Boolean(device?.reservation) && !mine && !observing;
  const { data: myRequest } = useQuery({
    ...trpc.device.myJoinRequest.queryOptions({ deviceId }),
    enabled: canAsk,
    refetchInterval: pollInterval,
  });
  const awaitingAnswer = canAsk && myRequest?.state === "pending";

  // A popout takes the stream; this tab keeps the reservation and the page.
  const { poppedOut, reclaim } = usePopoutPresence(deviceId);

  const invalidate = useCallback(() => {
    qc.invalidateQueries({ queryKey: trpc.device.get.queryKey({ id: deviceId }) });
    qc.invalidateQueries({ queryKey: trpc.device.list.queryKey() });
    qc.invalidateQueries({ queryKey: trpc.device.myJoinRequest.queryKey({ deviceId }) });
  }, [qc, deviceId]);

  // The reservation is gone from `device.get` by the time the revocation is
  // explained, so the id has to be kept while it is still there.
  const heldReservation = useRef<string | undefined>(undefined);
  if (device?.reservation?.id) heldReservation.current = device.reservation.id;

  /**
   * Set before the release request goes out, not after it returns.
   *
   * Letting go of a device revokes the session like any other release, so the
   * console reports it exactly as it reports being kicked — and the revoke can
   * arrive over the socket before the mutation resolves. A user who clicked
   * Release does not need to be told their session ended.
   */
  const releasedHere = useRef(false);

  const { ended, reportEnded } = useSessionEnded(inSession, releasedHere);
  const onRevoked = useCallback(
    (reason?: string) => {
      // The header still offered "Release" for a device the user no longer has.
      invalidate();
      reportEnded(reason);
    },
    [invalidate, reportEnded],
  );

  const reserve = useMutation(
    trpc.device.reserve.mutationOptions({
      onSuccess: () => {
        toast.success("Device reserved");
        invalidate();
      },
      onError: (e) => toast.error(e.message),
    }),
  );

  const release = useMutation(
    trpc.device.release.mutationOptions({
      onMutate: () => {
        releasedHere.current = true;
      },
      onSuccess: () => {
        toast.success("Device released");
        invalidate();
        // There is nothing left on this page to look at: no session, and a
        // device anyone can now take.
        navigate({ to: "/devices" });
      },
      onError: (e) => {
        releasedHere.current = false;
        toast.error(e.message);
      },
    }),
  );

  const forceRelease = useMutation(
    trpc.admin.forceRelease.mutationOptions({
      onSuccess: () => {
        toast.success("Device taken back");
        invalidate();
      },
      onError: (e) => toast.error(e.message),
    }),
  );

  const joinSession = useMutation(
    trpc.admin.joinSession.mutationOptions({
      onSuccess: invalidate,
      onError: (e) => toast.error(e.message),
    }),
  );

  const leaveSession = useMutation(
    trpc.device.leaveSession.mutationOptions({
      onSuccess: invalidate,
      onError: (e) => toast.error(e.message),
    }),
  );

  const requestJoin = useMutation(
    trpc.device.requestJoin.mutationOptions({
      onSuccess: () => {
        toast.success("Asked to join. Waiting for an answer.");
        invalidate();
      },
      onError: (e) => toast.error(e.message),
    }),
  );

  const cancelJoinRequest = useMutation(
    trpc.device.cancelJoinRequest.mutationOptions({
      onSuccess: invalidate,
      onError: (e) => toast.error(e.message),
    }),
  );

  const answerJoinRequest = useMutation(
    trpc.device.answerJoinRequest.mutationOptions({
      onSuccess: invalidate,
      onError: (e) => toast.error(e.message),
    }),
  );

  const answerAdbAuth = useMutation(
    trpc.device.answerAdbAuthRequest.mutationOptions({
      onSuccess: (result) => {
        toast.success(
          result.approved
            ? "Key approved and added to your account"
            : "Key denied — the connection was closed",
        );
        invalidate();
      },
      onError: (e) => toast.error(e.message),
    }),
  );

  // An approval announces itself — the console simply opens. Being turned down
  // or timing out does not, so say it once, on the transition.
  const lastAnswer = useRef<string | null>(null);
  const requestState = myRequest?.state ?? null;
  useEffect(() => {
    const previous = lastAnswer.current;
    lastAnswer.current = requestState;
    if (previous !== "pending") return;
    if (requestState === "denied") toast.error("Your request to join was declined");
    if (requestState === "expired") toast("Your request to join went unanswered");
  }, [requestState]);

  // Only the holder renews. Another user's tab must not keep a device they do
  // not hold alive — but it still gets `idleDeadline`, which needs no renewal.
  const renewal = useReservationKeeper(device?.reservation, mine);

  const [detailsOpen, setDetailsOpen] = useState(() => loadPanelOpen("details"));
  const toggleDetails = () =>
    setDetailsOpen((open) => {
      savePanelOpen("details", !open);
      return !open;
    });

  // Below `lg` there is no Details flank — beside the sidebar and the rail it
  // would leave the screen no width — so the `⋯` menu opens it as a sheet.
  const [detailsSheetOpen, setDetailsSheetOpen] = useState(false);
  // Opened from the `⋯` menu, so it is controlled rather than its own trigger.
  const [forceReleaseOpen, setForceReleaseOpen] = useState(false);

  if (!device) return null;

  const owner = device.reservation?.ownerName ?? "Someone";
  const canForceRelease = Boolean(me?.isAdmin && device.reservation && !mine);
  const popOut = () =>
    openPopout(deviceId, {
      width: device.displayWidth,
      height: device.displayHeight,
      scale: device.displayScale,
    });

  const details = (
    <DeviceDetails
      device={device}
      mine={mine}
      owner={owner}
      idleTimeoutSeconds={policy?.idleTimeoutSeconds}
      renewal={renewal}
      onChanged={invalidate}
    />
  );

  // Who else is here, for the holder. Badges once; a dot and a word now, like
  // every other state on the page.
  const notices =
    mine && device.reservation ? (
      <>
        {device.reservation.observers.length > 0 && (
          <span className="inline-flex shrink-0 items-center gap-2">
            <StatusDot tone="warning" />
            {describeObservers(device.reservation.observers)} in this session
          </span>
        )}
        {/* A dialog dismissed by accident must not be the only way to answer. */}
        {device.reservation.joinRequests.length > 0 && (
          <span className="inline-flex shrink-0 items-center gap-2">
            <StatusDot tone="info" pulse />
            {device.reservation.joinRequests.length} waiting to join
          </span>
        )}
      </>
    ) : null;
  const hasNotices = Boolean(
    mine &&
      device.reservation &&
      (device.reservation.observers.length > 0 || device.reservation.joinRequests.length > 0),
  );

  return (
    // Locked to the viewport, so the screen gets every pixel the top bar and
    // the flanks do not: a device is used in portrait almost always, and the
    // old page spent its height on chrome and then scrolled. On a phone the
    // shell drops its bottom bar here, so the top bar's back button is the only
    // way out.
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {/* The page's name and its session actions live in the shell's top bar,
          as a Vercel deployment's do: there is no header row in the content
          to spend the screen's height on. */}
      <PageHeader
        title={device.name ?? device.id}
        back={{ to: "/devices", label: "Devices" }}
        meta={<DeviceStatusLabel status={device.status} mine={mine} className="text-label-13" />}
        actions={
          <div className="flex items-center gap-2">
            {/* A page action rather than a device one, so it sits with Release
                instead of in the rail: it is about this window, not the phone.
                Not offered on a phone, where there is no second window to have. */}
            {inSession && !poppedOut && (
              <>
                {/* Icon-only until `lg`: the top bar centres the page's name
                    between its two sides, and a wide right side shoves it off
                    centre at the widths where the bar is narrowest. */}
                <Button
                  variant="secondary"
                  size="icon-sm"
                  aria-label="Pop out"
                  title="Pop out"
                  className="hidden md:inline-flex lg:hidden"
                  onClick={popOut}
                >
                  <ExternalLink />
                </Button>
                <Button
                  variant="secondary"
                  size="small"
                  prefix={<ExternalLink />}
                  className="hidden lg:inline-flex"
                  onClick={popOut}
                >
                  Pop out
                </Button>
              </>
            )}
            {mine && (
              <Button
                variant="secondary"
                size="small"
                className="pointer-coarse:h-10"
                disabled={release.isPending}
                onClick={() => release.mutate({ deviceId })}
              >
                Release
              </Button>
            )}
            {/* Anyone who is in the session can step out of it, however they
                got there. */}
            {observing && (
              <Button
                variant="secondary"
                size="small"
                className="pointer-coarse:h-10"
                aria-label="Leave session"
                disabled={leaveSession.isPending}
                onClick={() => leaveSession.mutate({ deviceId })}
              >
                Leave<span className="hidden sm:inline">&nbsp;session</span>
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="tertiary"
                  size="icon-sm"
                  aria-label="More actions"
                  // Beside the Details flank the only thing in here is Force
                  // release; without it the menu would open empty.
                  className={cn("pointer-coarse:size-10", !canForceRelease && "lg:hidden")}
                >
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-52">
                <DropdownMenuItem className="lg:hidden" onSelect={() => setDetailsSheetOpen(true)}>
                  <Info /> Details
                </DropdownMenuItem>
                {canForceRelease && (
                  <>
                    <DropdownMenuSeparator className="lg:hidden" />
                    <DropdownMenuItem variant="error" onSelect={() => setForceReleaseOpen(true)}>
                      <LogOut /> Force release…
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
      />

      {mine && <ReservationKeeper renewal={renewal} />}
      <SessionEndedDialog
        reservationId={heldReservation.current}
        fallbackReason={ended?.reason}
        open={ended !== null}
        onDismiss={() => navigate({ to: "/devices" })}
      />

      {/* Disclosure, not a gate: the admin is already in the session by the
          time this renders, so blocking the holder would achieve nothing but
          getting in their way. */}
      {mine && device.reservation && (
        <ObserverDisclosure observers={device.reservation.observers} />
      )}

      {/* This one *is* a gate — nobody is in the session until it is answered. */}
      {mine && device.reservation && (
        <JoinRequestPrompt
          requests={device.reservation.joinRequests}
          pending={answerJoinRequest.isPending}
          onAnswer={(requestId, approve) => answerJoinRequest.mutate({ requestId, approve })}
        />
      )}

      {/* Also a gate: the connect sits parked on the developer's terminal
          until this is answered. */}
      {mine && device.reservation && (
        <AdbAuthPrompt
          requests={device.reservation.adbAuthRequests}
          pending={answerAdbAuth.isPending}
          onAnswer={(requestId, approve) => answerAdbAuth.mutate({ requestId, approve })}
        />
      )}

      {/* Taking a device from someone mid-session is disruptive and auditable,
          so it asks for a reason rather than being a one-click action next to
          Release. */}
      {canForceRelease && (
        <ForceReleaseDialog
          owner={device.reservation?.ownerName ?? "someone"}
          open={forceReleaseOpen}
          onOpenChange={setForceReleaseOpen}
          pending={forceRelease.isPending}
          onConfirm={(reason) => forceRelease.mutate({ deviceId, reason })}
        />
      )}

      <Dialog open={detailsSheetOpen} onOpenChange={setDetailsSheetOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Details</DialogTitle>
            <DialogDescription className="sr-only">
              What this device is, and the state of its session.
            </DialogDescription>
          </DialogHeader>
          {details}
        </DialogContent>
      </Dialog>

      {/* The session's facts, one line, wherever the Details flank is not
          drawn (below `lg`): the top bar has room for the name and the state,
          and these truncate rather than wrap, so they cost one line at any
          width. From `lg` the line is drawn only for the holder's notices —
          the flank carries the rest. */}
      <div
        className={cn(
          "-mt-0.5 flex min-w-0 items-center gap-x-4 text-gray-900 text-label-13",
          !hasNotices && "lg:hidden",
        )}
      >
        <DeviceFacts
          device={device}
          mine={mine}
          owner={owner}
          renewal={renewal}
          className="min-w-0 flex-1 lg:hidden"
        />
        {hasNotices && <div className="flex shrink-0 items-center gap-4">{notices}</div>}
      </div>

      <div className="flex min-h-0 flex-1 gap-3">
        {!inSession ? (
          device.reservation ? (
            <EmptyPanel
              icon={<Users />}
              title={`${owner} is using this device`}
              description={
                awaitingAnswer
                  ? "Your request to join is waiting for an answer."
                  : me?.isAdmin
                    ? "Join to see the screen and control it alongside them, or wait for it to come free."
                    : "Ask to join, or wait for it to come free."
              }
            >
              {/* Joining is the gentler of the two: the holder keeps the device
                  and is told, rather than losing it mid-task. An admin does
                  that directly; everyone else asks. */}
              {me?.isAdmin ? (
                <Button
                  className="h-10"
                  loading={joinSession.isPending}
                  onClick={() => joinSession.mutate({ deviceId })}
                >
                  Join session
                </Button>
              ) : awaitingAnswer ? (
                <div className="flex flex-wrap items-center justify-center gap-4">
                  <span className="inline-flex items-center gap-2 text-gray-900 text-label-14">
                    <StatusDot tone="info" pulse />
                    Waiting for {device.reservation.ownerName}…
                  </span>
                  <Button
                    variant="secondary"
                    className="h-10"
                    disabled={cancelJoinRequest.isPending}
                    onClick={() => cancelJoinRequest.mutate({ deviceId })}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button
                  className="h-10"
                  loading={requestJoin.isPending}
                  onClick={() => requestJoin.mutate({ deviceId })}
                >
                  Ask to join
                </Button>
              )}
            </EmptyPanel>
          ) : device.status === "cleaning" ? (
            <EmptyPanel
              icon={<Sparkles />}
              title="Being cleaned"
              description="It is being reset after its last session, and will be available in a moment."
            >
              <DeviceStatusLabel status="cleaning" className="text-gray-900" />
            </EmptyPanel>
          ) : (
            // The reserve action lives here rather than in the top bar: this
            // empty panel is where the eye already is, and it is the only
            // thing the page is asking for.
            <EmptyPanel
              icon={<Smartphone />}
              title="This device is free"
              description="Reserve it to open a session and start streaming."
            >
              <Button
                className="h-10"
                loading={reserve.isPending}
                onClick={() => reserve.mutate({ deviceId })}
              >
                Reserve this device
              </Button>
            </EmptyPanel>
          )
        ) : poppedOut ? (
          // Two decoders on one device is waste the user never asked for,
          // so this tab stands down while the popout has the stream.
          <EmptyPanel
            icon={<ExternalLink />}
            title="Open in a popout window"
            description="This tab steps aside while the popout has the stream."
          >
            <Button variant="secondary" className="h-10" onClick={reclaim}>
              Bring it back here
            </Button>
          </EmptyPanel>
        ) : (
          <DeviceConsole
            deviceId={deviceId}
            platform={device.platform}
            active
            admin={me?.isAdmin}
            className="min-w-0 flex-1"
            onRevoked={onRevoked}
          />
        )}

        <SidePanel
          side="right"
          open={detailsOpen}
          onToggle={toggleDetails}
          title="Details"
          width="w-80"
          className="hidden lg:flex"
        >
          <div className="p-4">{details}</div>
        </SidePanel>
      </div>
    </div>
  );
}

type DeviceDetail = RouterOutputs["device"]["get"];

/**
 * The session's facts as one muted line, for wherever the Details flank is not
 * drawn: what the device is, who holds it and until when. A phone keeps the
 * ones that say what it is looking at and whether it is about to lose it; the
 * rest is in Details.
 */
function DeviceFacts({
  device,
  mine,
  owner,
  renewal,
  className,
}: {
  device: DeviceDetail;
  mine: boolean;
  owner: string;
  renewal: ReservationRenewal;
  className?: string;
}) {
  const items: { key: string; node: ReactNode; phone?: boolean }[] = [
    {
      key: "os",
      node: `${platformLabel(device.platform)} ${device.osVersion ?? ""}`.trim(),
      phone: true,
    },
    { key: "model", node: device.model },
    // On a phone, who holds it says more than where it is plugged in.
    { key: "provider", node: device.provider.name, phone: !(device.reservation && !mine) },
    ...(device.reservation && !mine
      ? [{ key: "holder", node: `Held by ${owner}`, phone: true }]
      : []),
    ...(device.reservation
      ? [
          {
            key: "until",
            node: (
              <span className="tabular-nums">
                Reserved until{" "}
                <span className="text-gray-1000">{formatTime(device.reservation.expiresAt)}</span>
              </span>
            ),
          },
        ]
      : []),
    // Only once the deadline is near: a countdown ticking all session long,
    // resetting at every touch, is noise.
    ...(renewal.nearing && renewal.idleDeadline !== null
      ? [
          {
            key: "idle",
            node: (
              <span className="font-medium text-amber-900 tabular-nums">
                Releases in <Countdown deadline={renewal.idleDeadline} />
              </span>
            ),
            phone: true,
          },
        ]
      : []),
    ...(device.streamCodec
      ? [{ key: "codec", node: <span className="text-label-13-mono">{device.streamCodec}</span> }]
      : []),
  ].filter((item) => Boolean(item.node));

  return (
    <p className={cn("truncate", className)}>
      {items.map((item, index) => (
        // The separator belongs to the item after it, so an item a phone hides
        // takes its own dot with it.
        <span key={item.key} className={cn(!item.phone && "hidden md:inline")}>
          {index > 0 && <span className="px-2 text-gray-500">·</span>}
          {item.node}
        </span>
      ))}
    </p>
  );
}

/** `11:21 PM` — a reservation's end, to the minute. */
function formatTime(date: string | Date): string {
  return new Date(date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/**
 * What the screen's place becomes when there is no session to show — free,
 * being cleaned, someone else's, or popped out: Geist's empty state, in a card
 * the size of the screen it stands in for, with the one thing the page is
 * asking for.
 */
function EmptyPanel({
  icon,
  title,
  description,
  children,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex min-h-0 min-w-0 flex-1 overflow-y-auto rounded-lg bg-background-100 shadow-border">
      <EmptyState
        border={false}
        icon={icon}
        title={title}
        description={description}
        className="m-auto bg-transparent"
      >
        {children}
      </EmptyState>
    </div>
  );
}

/**
 * Everything known about the device, as Vercel's deployment metadata: grey
 * labels over values, two to a row. The desktop's Details flank, and on a
 * phone part of the console's sheet (or a sheet of its own from the `⋯` menu
 * when there is no session).
 */
function DeviceDetails({
  device,
  mine,
  owner,
  idleTimeoutSeconds,
  renewal,
  onChanged,
}: {
  device: DeviceDetail;
  mine: boolean;
  owner: string;
  idleTimeoutSeconds: number | null | undefined;
  renewal: ReservationRenewal;
  onChanged: () => void;
}) {
  return (
    <div className="grid gap-5">
      <MetaGrid>
        <Meta label="Status">
          <DeviceStatusLabel status={device.status} mine={mine} />
        </Meta>
        {device.reservation && !mine && <Meta label="Held by">{owner}</Meta>}
        {device.reservation && (
          <Meta label="Reserved until" title={new Date(device.reservation.expiresAt).toString()}>
            <span className="tabular-nums">{formatTime(device.reservation.expiresAt)}</span>
          </Meta>
        )}
        {idleTimeoutSeconds != null && renewal.idleDeadline !== null && (
          <Meta label="Idle timeout">
            {/* Only once the deadline is near: a countdown ticking all
                session long, resetting at every touch, is noise. */}
            {renewal.nearing ? (
              <span className="text-amber-900 tabular-nums">
                <Countdown deadline={renewal.idleDeadline} /> left
              </span>
            ) : (
              `${Math.round(idleTimeoutSeconds / 60)} min`
            )}
          </Meta>
        )}
        <Meta label="Codec" mono>
          {device.streamCodec}
        </Meta>
        <Meta label="Last seen">{relativeTime(device.presentAt)}</Meta>
      </MetaGrid>

      <MetaGrid divided>
        <Meta label="Model">{device.model}</Meta>
        <Meta label="OS">
          {`${platformLabel(device.platform)} ${device.osVersion ?? ""}`.trim()}
        </Meta>
        <Meta label="Provider" wide>
          {device.provider.name}
        </Meta>
        <Meta label="Manufacturer">{device.manufacturer}</Meta>
        {/* `serial` and `brand` are the same as the identifier and the
            manufacturer on every device seen so far — `ro.serialno` *is* the
            adb serial, and `ro.product.brand` is usually the vendor again.
            Shown only when a device disagrees, so the panel carries no row
            that repeats another. */}
        <Meta label="Brand">{differing(device.brand, device.manufacturer)}</Meta>
        <Meta label="Display">
          {device.displayWidth ? (
            <span className="tabular-nums">
              {device.displayWidth}×{device.displayHeight}
              {device.displayScale ? ` @${device.displayScale}x` : ""}
              {device.displayRotation ? ` · ${device.displayRotation}°` : ""}
            </span>
          ) : null}
        </Meta>
        <Meta label="Battery">
          {device.batteryLevel == null ? null : (
            <span className="tabular-nums">
              {Math.round(device.batteryLevel * 100)}%
              {device.batteryState && (
                <span className="text-gray-900"> · {device.batteryState}</span>
              )}
            </span>
          )}
        </Meta>
        <Meta label="ABI" mono>
          {device.abi}
        </Meta>
        <Meta label="SDK">{device.sdk?.toString()}</Meta>
        <Meta label="Security patch">
          {device.securityPatch && <span className="tabular-nums">{device.securityPatch}</span>}
        </Meta>
        <Meta label="Identifier" mono wide>
          {device.id}
        </Meta>
        <Meta label="Serial" mono wide>
          {differing(device.serial, device.id)}
        </Meta>
      </MetaGrid>

      {/* Remote debugging is the holder's tool, not an admin one: it hands
          out a live adb transport to whoever runs the command. */}
      {mine && device.platform === "android" && (
        <RemoteDebugging
          deviceId={device.id}
          port={device.adbPort}
          host={hostOf(device.provider.publicBaseUrl)}
          onChanged={onChanged}
        />
      )}
    </div>
  );
}

/** Two label/value pairs to a row; `divided` rules it off from the group above. */
function MetaGrid({ divided = false, children }: { divided?: boolean; children: ReactNode }) {
  return (
    <dl
      className={cn(
        "grid grid-cols-2 gap-x-4 gap-y-5",
        divided && "border-gray-alpha-400 border-t pt-5",
      )}
    >
      {children}
    </dl>
  );
}

/**
 * One of Vercel's label-over-value pairs. Renders nothing for a value the
 * device did not report, so the grid closes up rather than showing dashes.
 */
function Meta({
  label,
  mono = false,
  wide = false,
  title,
  children,
}: {
  label: string;
  /** Identifiers, codecs and ABIs: things that are read character by character. */
  mono?: boolean;
  /** A value too long for half the panel. */
  wide?: boolean;
  /** Hover text, when the visible value is not the whole of it. */
  title?: string;
  children?: ReactNode;
}) {
  if (children == null || children === false || children === "") return null;
  return (
    <div className={cn("grid min-w-0 content-start gap-2", wide && "col-span-2")}>
      <dt className="text-gray-900 text-label-13">{label}</dt>
      {/* `min-w-0` is what makes `truncate` work at all inside a grid cell:
          without it the value sets the column's minimum width and pushes
          itself out of the panel instead of being cut short. */}
      <dd
        className={cn(
          "min-w-0 truncate text-gray-1000",
          mono ? "text-label-13-mono leading-5" : "text-label-14",
        )}
        title={title ?? (typeof children === "string" ? children : undefined)}
      >
        {children}
      </dd>
    </div>
  );
}

type Observer = { userId: string; name: string | null; joinedAt: string | Date };

/** "Ana Silva", or "Ana Silva and 2 others" — a one-line note, not a list. */
function describeObservers(observers: Observer[]): string {
  const [first, ...rest] = observers;
  const name = first?.name ?? "An admin";
  if (rest.length === 0) return name;
  return `${name} and ${rest.length} other${rest.length === 1 ? "" : "s"}`;
}

/**
 * Tells the holder, once, that somebody joined.
 *
 * Non-blocking on purpose: an admin who joined is already there and already has
 * control, so a modal the holder must clear would be theatre. The note above the
 * screen is the persistent half — this fires only on the transition from nobody
 * to somebody, so a page refresh does not re-announce a session that has had an
 * observer in it for an hour.
 */
function ObserverDisclosure({ observers }: { observers: Observer[] }) {
  const [open, setOpen] = useState(false);
  const [arrival, setArrival] = useState<Observer | null>(null);

  /**
   * Who has already been announced, in a ref rather than state.
   *
   * The first version tracked this in state with `observers` in the dependency
   * array, which loops the moment that array's identity churns between renders:
   * the effect sets state, the state is a dependency, and round it goes. It
   * only held together because react-query's structural sharing happened to
   * keep the reference stable, which is far too subtle a thing to rest a render
   * loop on.
   */
  const announced = useRef<string[]>([]);
  const latest = useRef(observers);
  latest.current = observers;

  // A sorted id list: a primitive, so the effect re-runs when the *people*
  // change and never merely because a new array arrived saying the same thing.
  const present = observers
    .map((o) => o.userId)
    .sort()
    .join(",");

  useEffect(() => {
    const fresh = latest.current.find((o) => !announced.current.includes(o.userId));
    // Someone leaving makes them announceable again if they come back.
    announced.current = present ? present.split(",") : [];
    if (!fresh) return;
    setArrival(fresh);
    setOpen(true);
  }, [present]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{arrival?.name ?? "An admin"} joined this session</DialogTitle>
          <DialogDescription>
            They can see the screen and control the device, the same as you. The device is still
            yours — this is not a release.
          </DialogDescription>
        </DialogHeader>
        {/* A lone acknowledgement spans the footer, secondary, as Geist's
            single-action modal does: there is nothing here to confirm. */}
        <DialogFooter>
          <Button variant="secondary" onClick={() => setOpen(false)}>
            Got it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type JoinRequest = { id: string; userId: string; name: string | null; note: string | null };

type AdbAuthRequest = {
  requestId: string;
  fingerprint: string;
  comment: string | null;
  /** Serialised over tRPC, like every other date this page renders. */
  askedAt: string;
  expiresAt: string;
};

/**
 * An `adb connect` carrying a key nobody has registered.
 *
 * Approving adds the key to *your* account, which is the only thing the holder
 * can honestly assert: they know somebody is at the door, not who. Somebody
 * else's key belongs on that person's account, added in their own settings.
 *
 * A dialog, and one request at a time, for the same reason as
 * `JoinRequestPrompt`: the connect is parked until this is answered, and an
 * inline card at the top of the page is easy to miss while the 120-second
 * window runs out. Dismissing it lets the request lapse — there is no note
 * above the screen to fall back to, because the answer is only worth anything
 * inside that window.
 */
function AdbAuthPrompt({
  requests,
  pending,
  onAnswer,
}: {
  requests: AdbAuthRequest[];
  pending: boolean;
  onAnswer: (requestId: string, approve: boolean) => void;
}) {
  const [dismissed, setDismissed] = useState<string[]>([]);
  const next = requests.find((r) => !dismissed.includes(r.requestId));

  return (
    <Dialog
      open={Boolean(next)}
      onOpenChange={(open) => {
        if (!open && next) setDismissed((ids) => [...ids, next.requestId]);
      }}
    >
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>An adb key is asking to use this device</DialogTitle>
          <DialogDescription>
            Approve only if this is your own machine. The key is added to your account, so your next
            connect anywhere is silent — and every `adb shell` through it is attributed to you.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-2">
          <div className="flex h-10 min-w-0 items-center rounded-md bg-background-100 px-3 shadow-border">
            <code className="truncate text-gray-1000 text-label-13-mono">{next?.fingerprint}</code>
          </div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-gray-900 text-label-13">
            {next?.comment && <span className="min-w-0 truncate">from {next.comment}</span>}
            <div className="flex-1" />
            {/* A request whose window has closed is refused on the provider
                whatever this page shows, so the countdown is not decoration. */}
            {next && (
              <span className="text-gray-1000 tabular-nums">
                <Countdown deadline={next.expiresAt} /> left
              </span>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="secondary"
            disabled={pending}
            onClick={() => next && onAnswer(next.requestId, false)}
          >
            Deny
          </Button>
          <Button loading={pending} onClick={() => next && onAnswer(next.requestId, true)}>
            Approve, it is mine
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Asks the holder to answer the oldest outstanding request.
 *
 * Deliberately unlike `ObserverDisclosure`, which announces something already
 * true and can be waved away: nobody is in the session until this is answered,
 * so it has two real buttons and no "Got it". One at a time — a queue of people
 * is rare, and answering them one by one is clearer than a list of button
 * pairs. Closing it falls back to the note above the screen.
 */
function JoinRequestPrompt({
  requests,
  pending,
  onAnswer,
}: {
  requests: JoinRequest[];
  pending: boolean;
  onAnswer: (requestId: string, approve: boolean) => void;
}) {
  const [dismissed, setDismissed] = useState<string[]>([]);
  const next = requests.find((r) => !dismissed.includes(r.id));

  return (
    <Dialog
      open={Boolean(next)}
      onOpenChange={(open) => {
        if (!open && next) setDismissed((ids) => [...ids, next.id]);
      }}
    >
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{next?.name ?? "Someone"} wants to join this session</DialogTitle>
          <DialogDescription>
            {next?.note ? `“${next.note}” — ` : ""}
            If you let them in they can see the screen and control the device, the same as you. The
            device stays yours — this is not a release.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="secondary"
            disabled={pending}
            onClick={() => next && onAnswer(next.id, false)}
          >
            Decline
          </Button>
          <Button loading={pending} onClick={() => next && onAnswer(next.id, true)}>
            Let them in
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The provider's host, not its URL: whatever proxy fronts the web origin does
 * not forward a raw adb transport, so what a developer needs is the bare host
 * the provider itself bound the port on.
 */
function hostOf(baseUrl: string): string {
  try {
    return new URL(baseUrl).hostname;
  } catch {
    return baseUrl;
  }
}

/**
 * `adb connect` against a reserved device.
 *
 * The transport has existed since phase 4 and nothing in the UI ever reached
 * it. Exposing it is deliberately explicit — it opens a real adb port on the
 * provider — and it stays open until it is turned off or the reservation ends.
 */
function RemoteDebugging({
  deviceId,
  port,
  host,
  onChanged,
}: {
  deviceId: string;
  port: number | null;
  host: string;
  onChanged: () => void;
}) {
  const expose = useMutation(
    trpc.device.adbExpose.mutationOptions({
      onSuccess: (data) => {
        toast.success(`adb listening on ${data.connectString}`);
        onChanged();
      },
      onError: (e) => toast.error(e.message),
    }),
  );

  const unexpose = useMutation(
    trpc.device.adbUnexpose.mutationOptions({
      onSuccess: () => {
        toast.success("Remote debugging disabled");
        onChanged();
      },
      onError: (e) => toast.error(e.message),
    }),
  );

  const connectString = port ? `${host}:${port}` : null;
  const titleId = useId();
  const descriptionId = useId();

  const copy = async () => {
    if (!connectString) return;
    try {
      await navigator.clipboard.writeText(`adb connect ${connectString}`);
      toast.success("Copied");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not copy");
    }
  };

  return (
    // A settings row in Vercel's manner: the title and its switch on one line,
    // what it does underneath, and the result — the command to run — as a
    // snippet once there is one.
    <section className="grid gap-3 border-gray-alpha-400 border-t pt-5">
      <div className="flex items-center justify-between gap-3">
        <h3 id={titleId} className="font-medium text-gray-1000 text-label-14">
          Remote debugging
        </h3>
        <Switch
          size="medium"
          aria-labelledby={titleId}
          aria-describedby={descriptionId}
          checked={Boolean(connectString)}
          disabled={expose.isPending || unexpose.isPending}
          onCheckedChange={(on) =>
            on ? expose.mutate({ deviceId }) : unexpose.mutate({ deviceId })
          }
        />
      </div>
      <p id={descriptionId} className="text-copy-13 text-gray-900">
        {connectString
          ? "Listening for this reservation, until you turn it off or release the device."
          : "Opens an adb port on the provider for this reservation, until you turn it off or release the device."}
      </p>
      {connectString && (
        <div className="flex h-10 min-w-0 items-center rounded-md bg-background-100 pr-1 pl-3 shadow-border">
          <code className="min-w-0 flex-1 truncate text-gray-1000 text-label-13-mono">
            adb connect {connectString}
          </code>
          <Button
            variant="tertiary"
            size="icon-sm"
            onClick={copy}
            title="Copy to clipboard"
            aria-label="Copy the adb connect command"
            className="pointer-coarse:size-10"
          >
            <Copy />
          </Button>
        </div>
      )}
    </section>
  );
}

/**
 * Force-release, with the friction it deserves.
 *
 * The holder's session drops the moment this lands — `session.revoke` is a push
 * — so the reason is what they will be told and what the audit log keeps.
 */
function ForceReleaseDialog({
  owner,
  open,
  onOpenChange,
  pending,
  onConfirm,
}: {
  owner: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pending: boolean;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState("");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Take this device back?</DialogTitle>
          <DialogDescription>
            {owner} is using it right now. Their session ends immediately, and this is recorded in
            the audit log.
          </DialogDescription>
        </DialogHeader>
        {/* The part the admin acts on, banded as Vercel's destructive modals
            band their confirmation field. */}
        <DialogInset className="grid gap-2">
          <Label htmlFor="force-reason">Reason</Label>
          <Input
            id="force-reason"
            value={reason}
            placeholder="Needed for a release build"
            className="h-10"
            onChange={(event) => setReason(event.target.value)}
          />
        </DialogInset>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="error"
            disabled={pending}
            onClick={() => {
              onConfirm(reason.trim() || "force-released by admin");
              onOpenChange(false);
              setReason("");
            }}
          >
            Take it back
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * `value` unless it says the same thing as `against`, in which case nothing.
 *
 * Case-insensitive because the two sources disagree on it — `ro.product.brand`
 * answers `samsung` where `ro.product.manufacturer` answers `Samsung`.
 */
function differing(value: string | null | undefined, against: string | null | undefined) {
  if (!value) return null;
  return value.toLowerCase() === (against ?? "").toLowerCase() ? null : value;
}
